import { Link } from 'react-router-dom';
import type { Role } from '@cannery/shared';
import { useAuth } from '../../auth/AuthContext';
import { Badge, Card } from '../../components/ui';
import { Icon } from '../../components/Icon';

const WORKFLOW = ['Pending', 'Washing', 'Folding', 'Ready', 'Released', 'Done'] as const;

function areaMeta(role: Role) {
  return role === 'WASHING_EMPLOYEE'
    ? {
        area: 'Washing',
        path: '/wash/area',
        icon: 'washing' as const,
        steps: [
          'Receive laundry handed over from receiving',
          'Physically count and verify the items',
          'Wash: initial / hand wash, machine, drying',
          'Add extra drying when needed',
          'Hand over to the folding area',
        ],
        highlight: 'Washing',
      }
    : {
        area: 'Folding',
        path: '/fold/area',
        icon: 'folding' as const,
        steps: [
          'Receive laundry handed over from washing',
          'Cross-check the item count against expected',
          'Fold and prepare for pickup',
          'Mark READY for customer pickup / delivery',
        ],
        highlight: 'Folding',
      };
}

export function EmployeeDashboardPage({ role }: { role: Role }) {
  const { user, homePath } = useAuth();
  const meta = areaMeta(role);
  const today = new Date().toLocaleDateString('en-PH', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  return (
    <div className="space-y-5">
      <Card className="bg-gradient-to-br from-accent-deep to-accent-deeper p-6 text-white">
        <p className="text-sm font-medium text-indigo-100">{today}</p>
        <h1 className="mt-1 text-3xl font-bold tracking-tight">
          Hello, {user?.name?.split(' ')[0] ?? ''}
        </h1>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <Badge tone="slate" className="bg-white/15 text-white ring-white/25">
            {meta.area} Employee
          </Badge>
          {user?.branchCode && (
            <Badge tone="slate" className="bg-white/15 text-white ring-white/25">
              {user.branchCode}
            </Badge>
          )}
        </div>
      </Card>

      <Link to={meta.path} className="block">
        <Card className="flex items-center gap-4 p-5 transition-shadow hover:shadow-md">
          <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-indigo-100 text-indigo-600">
            <Icon name={meta.icon} className="h-7 w-7" />
          </span>
          <span className="min-w-0 flex-1">
            <span className="block text-lg font-semibold text-slate-900">Open {meta.area} Area</span>
            <span className="block text-sm text-slate-500">
              Your queue and verification tasks live here
            </span>
          </span>
          <Icon name="chevronDown" className="h-6 w-6 shrink-0 rotate-[-90deg] text-slate-400" />
        </Card>
      </Link>

      <Card className="p-5">
        <div className="flex items-center gap-2">
          <Icon name="shield" className="h-5 w-5 text-slate-400" />
          <h2 className="font-semibold text-slate-900">Laundry workflow</h2>
        </div>
        <ol className="mt-4 flex flex-wrap items-center gap-2">
          {WORKFLOW.map((step) => {
            const active = step === meta.highlight;
            return (
              <li
                key={step}
                className={`flex items-center gap-2 rounded-xl px-3 py-2 text-sm font-semibold ${
                  active
                    ? 'bg-accent text-accent-ink'
                    : 'bg-slate-100 text-slate-500'
                }`}
              >
                {active && <Icon name="check" className="h-4 w-4" />}
                {step}
              </li>
            );
          })}
        </ol>
        <p className="mt-4 text-sm text-slate-500">
          Your area queue verifies item counts at every stage — discrepancies stay visible in the
          transaction history.
        </p>
      </Card>
    </div>
  );
}